type StackFrameLike = { filename?: string; lineno?: number };

type ExceptionValueLike = {
  stacktrace?: { frames?: StackFrameLike[] };
};

export type ExtensionErrorCandidate = {
  exception?: { values?: ExceptionValueLike[] };
  extra?: Record<string, unknown>;
};

const EXTENSION_URL =
  /^(?:chrome|chrome-untrusted|moz|safari-web|safari|ms-browser)-extension:\/\//i;

const URL_IN_STACK = /[a-z][a-z0-9+.-]*:\/\/[^\s)'"]+/gi;

/**
 * True when every URL in `urls` points into an extension bundle, e.g.
 * `["chrome-extension://abc/inject.js:1:2"] → true`. An empty list is false so
 * a stack we could not attribute is never dropped.
 */
function onlyExtensionUrls(urls: string[]): boolean {
  return urls.length > 0 && urls.every((url) => EXTENSION_URL.test(url));
}

function rawStackStrings(event: ExtensionErrorCandidate): string[] {
  const extra = event.extra;
  if (!extra) return [];

  const reason = extra.reason as { stack?: unknown } | undefined;
  return [reason?.stack, extra.source].filter(
    (value): value is string => typeof value === "string",
  );
}

const ANONYMOUS_FRAME = "<anonymous>";

/**
 * An eval'd/injected script frame: `<anonymous>` filename WITH a line number.
 * Native builtin frames (`at JSON.parse (<anonymous>)`) also use the
 * `<anonymous>` filename but carry no line/col, so they don't match.
 */
function isInjectedFrame(frame: StackFrameLike): boolean {
  return frame.filename === ANONYMOUS_FRAME && typeof frame.lineno === "number";
}

/**
 * Detects errors thrown entirely inside anonymous injected/eval'd scripts
 * (extensions, bookmarklets, console snippets): every frame is an injected
 * `<anonymous>` frame except at most the outermost one, which is the Sentry
 * SDK's instrument wrapper. The wrapper's filename can't be matched — before
 * source maps are applied it is the app's own bundle URL — so it is exempted
 * by position (frames are ordered outermost first). Requires at least one
 * injected frame so an event with no stack at all is never dropped, and any
 * deeper first-party or native-builtin frame keeps the event.
 */
export function isAnonymousInjectedError(
  event: ExtensionErrorCandidate,
): boolean {
  const frames =
    event.exception?.values?.flatMap(
      (value) => value?.stacktrace?.frames ?? [],
    ) ?? [];

  return frames.some(isInjectedFrame) && frames.slice(1).every(isInjectedFrame);
}

/**
 * Detects errors thrown entirely inside a browser extension's injected
 * scripts. The page-level `window.onerror` / `window.onunhandledrejection`
 * handlers in `_app.tsx` forward these to Sentry even though no first-party
 * code is involved and nothing on our side can act on them.
 *
 * The SDK's RewriteFrames integration strips the extension origin off frame
 * filenames (`chrome-extension://<id>/injected-scripts/x.js` becomes
 * `app:///injected-scripts/x.js`), so the raw stack string captured in
 * `extra` is usually the only place the scheme survives.
 */
export function isBrowserExtensionError(
  event: ExtensionErrorCandidate,
): boolean {
  const frameUrls =
    event.exception?.values?.flatMap(
      (value) =>
        value?.stacktrace?.frames
          ?.map((frame) => frame.filename)
          .filter((filename): filename is string => Boolean(filename)) ?? [],
    ) ?? [];

  if (onlyExtensionUrls(frameUrls)) return true;

  return rawStackStrings(event).some((stack) =>
    onlyExtensionUrls(stack.match(URL_IN_STACK) ?? []),
  );
}
